/**
 * Marker functionality.
 *
 * THIS SOFTWARE IS PROVIDED "AS IS" AND WITHOUT ANY EXPRESS OR IMPLIED
 * WARRANTIES, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
 *
 * @copyright Copyright (c) 2018 - 2023 fusionLib. All rights reserved.
 * @link http://fusionlib.com
 */

import { flMaps } from "./fusionLib.maps.core.js";

/**
 * Maker object for use on the map.
 *
 * @param {flMaps.Map} map The map to show the marker on.
 * @param {number} lat number The latitude.
 * @param {number} lng number The longitude.
 * @param {string} content The HTML content for the marker.
 * @param {map} opts Optional options.
 */
flMaps.Overlay = function(map, lat, lng, content, opts) {
	opts = opts || {};
	opts.offset = opts.offset || [0, 0];
	this.id = opts.id || '';
	this.data = opts.data || '';

	var self = this;
	this.map = map;

	var el = document.createElement('div');

	if(opts.class) {
		el.className = opts.class;
	}

	el.style.borderStyle = "none";
	el.style.borderWidth = "0px";
	el.style.position = "absolute";
	el.innerHTML = content;

	if(this.map.driver === 'google') {

		/**
		 * Extend google overlays to do custom markers.
		 *
		 * @type {google.maps.OverlayView}
		 */
		Overlay.prototype = new google.maps.OverlayView();
		function Overlay(map, position, element) {
			var selfOvly = this;

			this.setValues({
				position: position,
				container: null,
				element: element,
				map: map
			});

			this.onAdd = function() {
				var overlaySelf = this,
					container = this.get('element');

				// If capturing click events
				if(opts.onClicked && !opts.draggable) {
					container.style.cursor = 'pointer';
					google.maps.event.addDomListener(container, 'click', function() {
						opts.onClicked(self);
					});
				}

				// If marker can be dragged
				if(opts.draggable) {
					container.draggable = true;
					container.isDragging = false;
					overlaySelf.set('savedCursor', container.style.cursor);

					google.maps.event.addDomListener(
						this.get('map').getDiv(),
						'mouseleave',
						function() {
							google.maps.event.trigger(container, 'mouseup');
						}
					);

					function mousedown(e) {
						overlaySelf.set('savedCursor', this.style.cursor);
						this.style.cursor = 'grabbing';
						container.isDragging = true;
						overlaySelf.map.set('draggable', false);
						overlaySelf.set('origin', e);
						overlaySelf.set('hasMoved', false);
					}

					function mousemove(e) {
						if(container.isDragging) {
							var origin = overlaySelf.get('origin'),
								ox = origin.targetTouches ? origin.touches[0].clientX : origin.clientX,
								oy = origin.targetTouches ? origin.touches[0].clientY : origin.clientY,
								nx = origin.targetTouches ? e.touches[0].clientX : e.clientX,
								ny = origin.targetTouches ? e.touches[0].clientY : e.clientY,
								left = ox - nx,
								top = oy - ny,
								protection = overlaySelf.getProjection(),
								pos = protection.fromLatLngToDivPixel(overlaySelf.get('position')),
								latLng = protection.fromDivPixelToLatLng(new google.maps.Point(pos.x - left, pos.y - top));

							if(Math.abs(left) + Math.abs(top) > 0)
								overlaySelf.set('hasMoved', true);

							overlaySelf.set('origin', e);
							overlaySelf.set('position', latLng);
							overlaySelf.draw();

							e.preventDefault();
							e.stopPropagation();
						}
					}

					function mouseup(e) {

						// If end drag the raise event
						if(container.isDragging) {
							container.isDragging = false;

							if(overlaySelf.get('hasMoved'))
								google.maps.event.trigger(selfOvly, 'dragend');
							else
								opts.onClicked(self);
						}

						overlaySelf.map.set('draggable', true);
						this.style.cursor = overlaySelf.get('savedCursor');
					}

					google.maps.event.addDomListener(
						container,
						'mousedown',
						mousedown
					);
					google.maps.event.addDomListener(
						container,
						'touchstart',
						mousedown
					);
					google.maps.event.addDomListener(
						overlaySelf.get('map').getDiv(),
						'mousemove',
						mousemove
					);
					google.maps.event.addDomListener(
						overlaySelf.get('map').getDiv(),
						'touchmove',
						mousemove
					);
					google.maps.event.addDomListener(
						container,
						'mouseup',
						mouseup
					);
					google.maps.event.addDomListener(
						container,
						'touchend',
						mouseup
					);
				}

				//this.getPanes().overlayLayer.appendChild(container);
				//this.getPanes().overlayMouseTarget.appendChild(container);
				this.getPanes().floatPane.appendChild(container);
			};

			this.draw = function() {
				var pos = this.getProjection().fromLatLngToDivPixel(this.get('position'));
				this.get('element').style.left = pos.x + opts.offset[0] + 'px';
				this.get('element').style.top = pos.y + opts.offset[1] + 'px';
				this.get('element').style.transform = 'translateX(-50%) translateY(-50%)';
			};

			this.onRemove = function() {
				this.get('element').parentNode.removeChild(this.get('container'));
				this.set('element', null)
			};
		}

		this._marker = new Overlay(
			map._map,
			new google.maps.LatLng(lat, lng),
			el
		);

		if(opts.onDragend) {
			this._marker.addListener('dragend', function() {
				opts.onDragend(self);
			});
		}
	}
	else {
		this._marker = new mapboxgl.Marker({
			element: el,
			draggable: opts.draggable || false,
			offset: opts.offset
		})
			.setLngLat([lng, lat])
			.addTo(this.map._map)

		if(opts.onDragend) {
			this._marker.on('dragend', function() {
				opts.onDragend(self);
			});
		}

		if(opts.onClicked) {
			el.style.cursor = 'pointer';
			el.addEventListener('click', function(e) {
				e.preventDefault();
				e.stopPropagation();
				opts.onClicked(self);
			});
		}
	}

	/**
	 * Get position of the marker.
	 *
	 * @returns {{lat:number, lng:number}}
	 */
	this.getPosition = function() {
		if(this.map.driver === 'google') {
			var p = this._marker.get('position');
			return { lat: p.lat(), lng: p.lng() };
		}
		else {
			return this._marker.getLngLat();
		}
	};

	/**
	 * Set the position of the marker.
	 *
	 * @param {number} lat The latitude.
	 * @param {number} lng The longitude.
	 * @returns {flMaps.Marker}
	 */
	this.setPosition = function(lat, lng) {
		if(this.map.driver === 'google') {
			this._marker.set('position', new google.maps.LatLng(lat, lng));
			this._marker.draw();
		}
		else {
			this._marker.setLngLat([lng, lat]);
		}
		return this;
	};

	/**
	 * Add the marker to a map.
	 * @param {flMaps.Map} map The map to add to.
	 * @returns {flMaps.Marker}
	 */
	this.addTo = function(map) {
		// Remove from the current map if any
		if(this.map)
			this.remove();

		this.map = map;
		if(this.map.driver === 'google') {
			this._marker.setMap(this.map._map);
		}
		else {
			this._marker.addTo(this.map._map);
		}

		return this;
	};

	/**
	 * Remove the marker from the map
	 */
	this.remove = function() {
		if(this.map.driver === 'google') {
			this._marker.setMap(null);
		}
		else {
			this._marker.remove();
		}
		this.map = null;
	};

	/**
	 * Get the marker HTML element.
	 * @returns {HTMLElement}
	 */
	this.getElement = function() {
		return el;
	}
};
